import React, { useState, useRef, useEffect, useMemo } from 'react';
import { defaultCountries, parseCountry, FlagImage } from 'react-international-phone';
import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface CountrySelectProps<T extends FieldValues> {
  label: string;
  field: ControllerRenderProps<T, Path<T>>;
  error?: string | undefined;
  disabled?: boolean;
  className?: string;
}

/**
 * CountrySelect Component
 *
 * A custom dropdown component for selecting countries with flags.
 * Integrates with react-hook-form via strictly typed Controller props.
 */
export const CountrySelect = <T extends FieldValues>({
  label,
  field,
  error,
  disabled,
  className,
}: CountrySelectProps<T>): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Process country data from the library
  const countries = useMemo(
    () =>
      defaultCountries.map((country) => {
        const parsed = parseCountry(country);
        return {
          name: parsed.name,
          iso2: parsed.iso2,
        };
      }),
    []
  );

  // Filter for search
  const filteredCountries = useMemo(
    () => countries.filter((country) => country.name.toLowerCase().includes(search.toLowerCase())),
    [countries, search]
  );

  // Find selected object
  const selectedCountry = countries.find((country) => country.name === field.value);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={clsx('flex w-full flex-col gap-2', className)} ref={containerRef}>
      <label className='font-sans text-14 font-bold text-gray-520'>{label}</label>

      <div className='relative'>
        {/* The Trigger (Looks like an Input) */}
        <button
          type='button'
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={clsx(
            'flex items-center justify-between',
            'h-[47px] w-full rounded-10 px-3',
            'border bg-white transition-all',
            error
              ? 'border-red-500'
              : isOpen
                ? 'border-brand-300 ring-1 ring-brand-300'
                : 'border-gray-200',
            disabled && 'cursor-not-allowed bg-gray-50 opacity-70'
          )}
        >
          <div className='flex items-center gap-3 overflow-hidden'>
            {selectedCountry ? (
              <>
                <FlagImage iso2={selectedCountry.iso2} size='24px' />
                <span className='truncate font-jakarta text-14 font-semibold text-brand-800'>
                  {selectedCountry.name}
                </span>
              </>
            ) : (
              <span className='font-jakarta text-14 text-gray-500'>Select Country...</span>
            )}
          </div>
          <ChevronDownIcon
            className={clsx('size-4 text-gray-500 transition-transform', isOpen && 'rotate-180')}
          />
        </button>

        {/* The Dropdown */}
        {isOpen && (
          <div className='absolute left-0 top-full z-50 mt-1 max-h-[250px] w-full overflow-hidden rounded-10 border border-gray-200 bg-white shadow-lg'>
            {/* Search Input inside dropdown */}
            <div className='sticky top-0 border-b border-gray-100 bg-gray-50 p-2'>
              <input
                autoFocus
                type='text'
                placeholder='Search...'
                className='w-full rounded-[6px] border border-gray-200 bg-white px-2 py-1 font-sans text-13 text-brand-800 outline-none placeholder:text-gray-500 focus:border-brand-300'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* List */}
            <div className='custom-scrollbar max-h-[200px] overflow-y-auto'>
              {filteredCountries.map((country) => (
                <button
                  key={country.iso2}
                  type='button'
                  onClick={() => {
                    field.onChange(country.name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={clsx(
                    'flex w-full items-center gap-3 px-3 py-2 hover:bg-gray-50',
                    country.name === field.value && 'bg-brand-50'
                  )}
                >
                  <FlagImage iso2={country.iso2} size='20px' />
                  <span className='text-left font-sans text-14 font-normal text-brand-800'>
                    {country.name}
                  </span>
                </button>
              ))}
              {filteredCountries.length === 0 && (
                <div className='p-3 text-center font-sans text-13 text-gray-500'>
                  No country found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <span className='text-xs font-medium text-red-500'>{error}</span>}
    </div>
  );
};
